/* ============================================================
   Rheal Bovine Collagen — Landing Page behaviour
   All initialisers are idempotent and re-run on
   shopify:section:load so the theme editor stays live.
   Each initialiser is scoped to a root element and guards
   against double-binding with a data flag.
   ============================================================ */
(function () {
  'use strict';

  /* ---------- Gallery (buy box) ---------- */
  function initGallery(root) {
    root.querySelectorAll('[data-lp-gallery]').forEach(function (g) {
      if (g.dataset.lpReady === '1') return;
      g.dataset.lpReady = '1';

      var stage = g.querySelector('[data-lp-stage]');
      var strip = g.querySelector('[data-lp-thumbs]');
      if (!stage || !strip) return;
      var thumbs = Array.prototype.slice.call(strip.querySelectorAll('[data-lp-thumb]'));
      if (!thumbs.length) return;

      function select(i, focus) {
        i = (i + thumbs.length) % thumbs.length;
        var t = thumbs[i];
        var src = t.getAttribute('data-full') || '';
        var alt = t.getAttribute('data-alt') || '';
        if (src) {
          stage.innerHTML = '<img src="' + src + '" alt="' + alt.replace(/"/g, '&quot;') + '" loading="eager">';
        }
        thumbs.forEach(function (x, xi) {
          x.classList.toggle('is-active', xi === i);
          x.setAttribute('aria-selected', xi === i ? 'true' : 'false');
        });
        g.dataset.lpIndex = String(i);
        // Slide the strip so the active thumb sits centred. This nudges the row
        // one-by-one as you click through (and reveals the neighbouring thumbs so
        // it's obvious there are more), in both directions. scrollTo clamps at the
        // ends, and does nothing when the row isn't overflowing (desktop).
        var target = t.offsetLeft - (strip.clientWidth - t.offsetWidth) / 2;
        strip.scrollTo({ left: target, behavior: 'smooth' });
        if (focus) t.focus();
      }

      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () { select(i); });
        t.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') { e.preventDefault(); select(i + 1, true); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); select(i - 1, true); }
        });
      });

      // touch swipe on the stage
      var x0 = null;
      stage.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        var cur = parseInt(g.dataset.lpIndex || '0', 10);
        if (Math.abs(dx) > 40) select(dx < 0 ? cur + 1 : cur - 1);
        x0 = null;
      }, { passive: true });

      select(parseInt(g.dataset.lpStart || '0', 10));
    });
  }

  /* ---------- Plan selector (buy box) ---------- */
  // Shopify's /discount/<code> route stores the code in a discount_code cookie
  // that re-applies it at checkout; expire it (on this host and the parent
  // domain) so an old code can't come back and stack with the new one.
  function clearDiscountCookie() {
    var host = window.location.hostname;
    var parent = host.split('.').slice(-2).join('.');
    ['', '; domain=' + host, '; domain=.' + host, '; domain=.' + parent].forEach(function (d) {
      document.cookie = 'discount_code=; Max-Age=0; path=/' + d;
    });
  }

  function initPlans(root) {
    root.querySelectorAll('[data-lp-plans]').forEach(function (wrap) {
      if (wrap.dataset.lpReady === '1') return;
      wrap.dataset.lpReady = '1';

      var cards = Array.prototype.slice.call(wrap.querySelectorAll('[data-lp-plan]'));
      var form = wrap.querySelector('[data-lp-form]');
      var idField = form ? form.querySelector('[data-lp-variant]') : null;
      var planField = form ? form.querySelector('[data-lp-selling-plan]') : null;
      var deliveryEl = wrap.querySelector('[data-lp-delivery]');
      var selected = null;

      function choose(card) {
        selected = card;
        cards.forEach(function (c) {
          var on = c === card;
          c.classList.toggle('is-selected', on);
          c.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        if (idField) idField.value = card.getAttribute('data-variant') || '';
        if (planField) planField.value = card.getAttribute('data-selling-plan') || '';
        if (deliveryEl) deliveryEl.textContent = card.getAttribute('data-delivery') || deliveryEl.textContent;
      }

      cards.forEach(function (c) {
        c.addEventListener('click', function () { choose(c); });
        c.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(c); }
        });
      });

      // Straight-to-checkout: build the line items (main product + selling plan
      // + any free gift variants for the chosen plan), add them, then jump the
      // customer straight to /checkout instead of the cart. No-ops safely until a
      // product is bound to the plan (data-variant is empty).
      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var card = selected || wrap.querySelector('[data-lp-plan].is-selected') || cards[0];
          if (!card) return;
          var variant = card.getAttribute('data-variant');
          if (!variant) { return; } // product not bound yet — do nothing rather than break

          var item = { id: Number(variant), quantity: 1 };
          var plan = card.getAttribute('data-selling-plan');
          if (plan) item.selling_plan = Number(plan);
          var items = [item];

          (card.getAttribute('data-gifts') || '').split(',').forEach(function (g) {
            var id = parseInt(g.trim(), 10);
            if (id) items.push({ id: id, quantity: 1 });
          });

          var btn = form.querySelector('[type="submit"]');
          if (btn) { btn.disabled = true; btn.dataset.lpBusy = '1'; btn.setAttribute('aria-busy', 'true'); }

          // Empty the cart first so the LP always checks out with exactly the
          // chosen plan + its gifts, never whatever the customer had before.
          clearDiscountCookie();
          fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
          }).then(function () {
            // Tag the cart as originating from a landing page.
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              // discount: '' removes any code already on the cart so a previous
              // plan's discount never carries over to this checkout.
              body: JSON.stringify({ attributes: { _from_lp: 'true' }, discount: '' })
            });
          }).then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: items })
            });
          }).then(function () {
            // Set exactly this plan's discount code on the cart (or none).
            // `discount` replaces every code already on the cart, so only one
            // code ever reaches checkout.
            var code = (card.getAttribute('data-discount') || '').trim();
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ discount: code })
            });
          }).then(function () {
            window.location.href = '/checkout';
          }).catch(function () {
            if (btn) { btn.disabled = false; delete btn.dataset.lpBusy; btn.removeAttribute('aria-busy'); }
            window.location.href = '/cart';
          });
        });
      }

      // One-time purchase link: add a single variant (no subscription) and go
      // straight to checkout. Falls back to the plain href if no variant is set.
      var once = wrap.querySelector('[data-lp-once]');
      if (once) {
        once.addEventListener('click', function (e) {
          var v = parseInt((once.getAttribute('data-lp-once') || '').trim(), 10);
          if (!v) return; // no variant configured — follow the href instead
          e.preventDefault();
          once.style.pointerEvents = 'none';
          // Clear the cart first — one-time purchase should check out on its own.
          clearDiscountCookie();
          fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
          }).then(function () {
            // Tag the cart as originating from a landing page.
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              // discount: '' removes any code already on the cart so a previous
              // plan's discount never carries over to this checkout.
              body: JSON.stringify({ attributes: { _from_lp: 'true' }, discount: '' })
            });
          }).then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: [{ id: v, quantity: 1 }] })
            });
          }).then(function () {
            window.location.href = '/checkout';
          }).catch(function () {
            once.style.pointerEvents = '';
            window.location.href = '/cart';
          });
        });
      }

      var initial = wrap.querySelector('[data-lp-plan].is-selected') || cards[0];
      if (initial) choose(initial);
    });
  }

  /* ---------- Offer anchor: where the CTAs stop scrolling ----------
     Every CTA links to #lp-offer, which sits on the buy box wrapper — the
     desktop landing spot (pill, gallery and heading all in view). On mobile the
     gallery stacks above the details and fills the screen on its own, so we
     re-target the scroll to the heading. The breakpoint matches the container
     query in lp-buybox-style (767px on the .rlp container), so we measure the
     buy box itself rather than the window. */
  var OFFER_GAP = 8;

  function offerScrollTop() {
    var bb = document.querySelector('[data-lp-offer]');
    if (!bb) return null;
    var el = bb.clientWidth < 768 ? (bb.querySelector('[data-lp-offer-mobile]') || bb) : bb;
    var top = el.getBoundingClientRect().top + window.pageYOffset;
    if (el === bb) {
      // Skip the section's own top padding so the stop sits on the content
      // (pill + gallery) instead of a band of empty background — and so no
      // sliver of the section above stays in view.
      var grid = bb.querySelector('.rlp-bb__grid');
      if (grid) top += parseFloat(getComputedStyle(grid).paddingTop) || 0;
    }
    return Math.max(top - OFFER_GAP, 0);
  }

  function scrollToOffer() {
    var top = offerScrollTop();
    if (top === null) return false;
    var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: top, behavior: smooth ? 'smooth' : 'auto' });
    return true;
  }

  function initOfferAnchor(root) {
    root.querySelectorAll('a[href*="#lp-offer"]').forEach(function (a) {
      if (a.dataset.lpReady === '1') return;
      a.dataset.lpReady = '1';
      a.addEventListener('click', function (e) {
        // Only take over same-page links; an absolute URL to another path (or a
        // different query string) should still navigate as normal.
        var href = a.getAttribute('href') || '';
        if (href.charAt(0) !== '#') {
          var url;
          try { url = new URL(a.href, window.location.href); } catch (err) { return; }
          if (url.pathname !== window.location.pathname || url.search !== window.location.search) return;
        }
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        if (scrollToOffer()) e.preventDefault();
      });
    });
  }

  /* ---------- Rails: arrow buttons + scroll dots ---------- */
  function initRails(root) {
    root.querySelectorAll('[data-lp-rail-group]').forEach(function (group) {
      if (group.dataset.lpReady === '1') return;
      group.dataset.lpReady = '1';

      var rail = group.querySelector('[data-lp-rail]');
      if (!rail) return;
      var prev = group.querySelector('[data-lp-prev]');
      var next = group.querySelector('[data-lp-next]');
      var dots = Array.prototype.slice.call(group.querySelectorAll('[data-lp-dot]'));

      function step() {
        var card = rail.firstElementChild;
        var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || '20') || 20;
        return card ? card.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
      }
      if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });

      if (dots.length) {
        rail.addEventListener('scroll', function () {
          var max = rail.scrollWidth - rail.clientWidth;
          var i = max <= 0 ? 0 : Math.round((rail.scrollLeft / max) * (dots.length - 1));
          dots.forEach(function (d, di) { d.classList.toggle('is-active', di === i); });
        }, { passive: true });
      }
    });
  }

  /* ---------- Timeline: progressive spine + tick dots ---------- */
  function initTimeline(root) {
    root.querySelectorAll('[data-lp-timeline]').forEach(function (tl) {
      if (tl.dataset.lpReady === '1') return;
      tl.dataset.lpReady = '1';

      var rows = Array.prototype.slice.call(tl.querySelectorAll('[data-lp-row]'));
      if (!rows.length) return;

      function paint(reached) {
        rows.forEach(function (row, i) {
          var dot = row.querySelector('[data-lp-dot-node]');
          var fill = row.querySelector('[data-lp-fill]');
          var on = i <= reached;
          if (dot) {
            dot.classList.toggle('is-reached', on);
            dot.textContent = on ? '✓' : '';
          }
          if (fill) fill.style.height = i < reached ? '100%' : (on ? '50%' : '0%');
        });
      }

      if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        paint(rows.length - 1);
        return;
      }
      var reached = -1;
      var io = new IntersectionObserver(function (entries) {
        var max = reached;
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var i = rows.indexOf(e.target);
          if (i > max) max = i;
        });
        if (max !== reached) { reached = max; paint(reached); }
      }, { rootMargin: '0px 0px -45% 0px', threshold: 0 });
      rows.forEach(function (r) { io.observe(r); });
    });
  }

  /* ---------- boot ---------- */
  function initAll(root) {
    root = root || document;
    try { initGallery(root); } catch (e) {}
    try { initPlans(root); } catch (e) {}
    try { initOfferAnchor(root); } catch (e) {}
    try { initRails(root); } catch (e) {}
    try { initTimeline(root); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target || document); });

  // Landing on the page with #lp-offer already in the URL (the header chip links
  // to the full URL, so it reloads): the browser jumps to the wrapper natively,
  // which is right on desktop and too early on mobile. Correct it once images
  // have laid out so the stop lands in the same place as an in-page CTA click.
  function correctOfferHash() {
    if (window.location.hash !== '#lp-offer') return;
    setTimeout(scrollToOffer, 0);
  }
  window.addEventListener('load', correctOfferHash);
  window.addEventListener('hashchange', correctOfferHash);
})();
